<template>
  <div class="px-4 sm:px-6 lg:px-8">
    <div class="w-full py-2">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">Invoice Validation</h1>
          <div class="mt-1 text-sm text-gray-500 space-y-1">
             <div class="flex items-center gap-2">
              <span v-if="!isLoading && !isCheckingInvoices">
                Displaying {{ families.length }} {{ families.length === 1 ? 'family' : 'families' }}
              </span>
              <span v-if="isLoading || isCheckingInvoices" class="ml-2 inline-flex items-center">
                <svg class="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="ml-1">{{ isLoading ? 'Loading students...' : 'Checking Xero invoices...' }}</span>
              </span>
            </div>
          </div>
        </div>
         <div class="flex items-center gap-4">
          <XeroConnect v-once />
          <a href="/billing/dashboard" class="text-sm font-semibold leading-6 text-gray-900">Dashboard <span aria-hidden="true">→</span></a>
        </div>
      </div>
      
      <!-- Compact Table -->
      <div class="mt-8 flow-root">
        <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table class="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0 cursor-pointer" @click="setSort('code')">
                    Family Code <span v-if="sortKey === 'code'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                  </th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer" @click="setSort('contact')">
                    Contacts <span v-if="sortKey === 'contact'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                  </th>
                   <th v-if="terms.length > 0" scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 cursor-pointer" @click="setSort(`term-${terms[0].term}-${terms[0].year}`)">
                    Term {{ terms[0].term }} {{ terms[0].year }} <span v-if="sortKey === `term-${terms[0].term}-${terms[0].year}`">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                  </th>
                  <th v-if="terms.length > 1" scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 cursor-pointer" @click="setSort(`term-${terms[1].term}-${terms[1].year}`)">
                    Term {{ terms[1].term }} {{ terms[1].year }} <span v-if="sortKey === `term-${terms[1].term}-${terms[1].year}`">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                  </th>
                  <th v-if="terms.length > 2" scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 cursor-pointer" @click="setSort(`term-${terms[2].term}-${terms[2].year}`)">
                    Term {{ terms[2].term }} {{ terms[2].year }} <span v-if="sortKey === `term-${terms[2].term}-${terms[2].year}`">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                  </th>
                  <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 cursor-pointer" @click="setSort('totalDue')">
                    Total Due <span v-if="sortKey === 'totalDue'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr v-for="family in sortedFamilies" :key="family.code">
                  <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">{{ family.code }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <div v-if="family.contact1Email">{{ family.contact1FirstName }} {{ family.contact1LastName }} ({{ family.contact1Email }})</div>
                    <div v-if="family.contact2Email">{{ family.contact2FirstName }} {{ family.contact2LastName }} ({{ family.contact2Email }})</div>
                  </td>
                  <td v-if="terms.length > 0" class="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                    <InvoiceStatus :invoice-id="getInvoiceIdForTerm(family, terms[0])" :invoice-data="invoiceDetails" />
                  </td>
                  <td v-if="terms.length > 1" class="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                     <InvoiceStatus :invoice-id="getInvoiceIdForTerm(family, terms[1])" :invoice-data="invoiceDetails" />
                  </td>
                  <td v-if="terms.length > 2" class="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                     <InvoiceStatus :invoice-id="getInvoiceIdForTerm(family, terms[2])" :invoice-data="invoiceDetails" />
                  </td>
                   <td class="whitespace-nowrap px-3 py-4 text-right text-sm font-bold text-gray-700">
                     {{ formatCurrency(family.totalDue) }}
                  </td>
                </tr>
                 <tr v-if="sortedFamilies.length === 0 && !isLoading">
                    <td colspan="6" class="text-center py-5 text-sm text-gray-500">No enrolled families found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import XeroConnect from '../components/XeroConnect.vue';
import InvoiceStatus from '../components/InvoiceStatus.vue';

const families = ref([]);
const studentsData = ref([]);
const isLoading = ref(false);
const isCheckingInvoices = ref(false);
const invoiceDetails = ref({});
const sortKey = ref('totalDue');
const sortOrder = ref('desc');

const terms = computed(() => {
    const getTerm = (date) => Math.floor((date.getMonth() + 3) / 3);
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentTerm = getTerm(now);

    const getPreviousTerm = (term, year) => {
        if (term === 1) {
            return { term: 4, year: year - 1 };
        }
        return { term: term - 1, year: year };
    };

    const term1 = { term: currentTerm, year: currentYear };
    const term2 = getPreviousTerm(term1.term, term1.year);
    const term3 = getPreviousTerm(term2.term, term2.year);

    return [term1, term2, term3].sort((a, b) => {
      if (b.year !== a.year) {
        return b.year - a.year;
      }
      return b.term - a.term;
    });
});

const getInvoiceIdForTerm = (family, termInfo) => {
    const invoiceField = `Term${termInfo.term}${termInfo.year}Invoice`;
    const studentWithInvoice = family.students.find(s => s.Invoices && s.Invoices[invoiceField]);
    return studentWithInvoice ? studentWithInvoice.Invoices[invoiceField] : null;
};

const formatCurrency = (value) => {
  if (typeof value !== 'number') {
    return new Intl.NumberFormat('en-VU', { style: 'currency', currency: 'VUV' }).format(0);
  }
  return new Intl.NumberFormat('en-VU', { style: 'currency', currency: 'VUV' }).format(value);
};

const setSort = (key) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortOrder.value = 'asc';
  }
};

const sortedFamilies = computed(() => {
  return [...families.value]
    .map(family => {
      const totalDue = terms.value.reduce((acc, term) => {
        const invoiceId = getInvoiceIdForTerm(family, term);
        const detail = invoiceDetails.value[invoiceId];
        return acc + (detail && !detail.error ? detail.amountDue : 0);
      }, 0);
      return { ...family, totalDue };
    })
    .sort((a, b) => {
      let valA, valB;

      if (sortKey.value === 'totalDue') {
        valA = a.totalDue;
        valB = b.totalDue;
      } else if (sortKey.value === 'code') {
        valA = a.code;
        valB = b.code;
      } else if (sortKey.value === 'contact') {
        valA = (a.contact1LastName || '').toLowerCase();
        valB = (b.contact1LastName || '').toLowerCase();
      } else if (sortKey.value.startsWith('term-')) {
        const termKey = sortKey.value.split('-').slice(1).join('-');
        const termInfo = terms.value.find(t => `${t.term}-${t.year}` === termKey);
        
        const getDue = (family) => {
          const invoiceId = getInvoiceIdForTerm(family, termInfo);
          const detail = invoiceDetails.value[invoiceId];
          return detail && !detail.error ? detail.amountDue : -1; // -1 to sort errors/NAs at the bottom
        };
        valA = getDue(a);
        valB = getDue(b);
      }

      if (valA < valB) {
        return sortOrder.value === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return sortOrder.value === 'asc' ? 1 : -1;
      }
      return 0;
    });
});

const fetchStudents = async () => {
  isLoading.value = true;
  try {
    const response = await axios.get('/api/getStudents');
    studentsData.value = response.data;
  } catch (error) {
    console.error('Error fetching students:', error);
    studentsData.value = [];
  } finally {
    isLoading.value = false;
  }
};

const organizeStudentsByFamily = () => {
    const familyMap = new Map();
    const enrolledStudents = studentsData.value.filter(s => s.status === 'Enrolled' && s.familyCode);

    enrolledStudents.forEach(student => {
        if (!familyMap.has(student.familyCode)) {
            familyMap.set(student.familyCode, {
                code: student.familyCode,
                students: [],
                contact1FirstName: student.contact1FirstName,
                contact1LastName: student.contact1LastName,
                contact1Email: student.contact1Email,
                contact2FirstName: student.contact2FirstName,
                contact2LastName: student.contact2LastName,
                contact2Email: student.contact2Email,
            });
        }
        familyMap.get(student.familyCode).students.push(student);
    });

    families.value = Array.from(familyMap.values()).sort((a, b) => a.code.localeCompare(b.code));
};

const fetchAllInvoiceDetails = async () => {
  isCheckingInvoices.value = true;
  const allInvoiceIds = new Set();
  
  families.value.forEach(family => {
    terms.value.forEach(term => {
      const invoiceId = getInvoiceIdForTerm(family, term);
      if (invoiceId) {
        allInvoiceIds.add(invoiceId);
      }
    });
  });

  if (allInvoiceIds.size === 0) {
    isCheckingInvoices.value = false;
    return;
  }
  
  try {
    const response = await axios.post('/api/getInvoicesBatch', { invoiceIds: [...allInvoiceIds] });
    invoiceDetails.value = response.data;
  } catch (error) {
    console.error('Error fetching batch invoice details:', error);
    // Mark all requested as errored
    [...allInvoiceIds].forEach(id => {
        if(!invoiceDetails.value[id]) {
            invoiceDetails.value[id] = { error: true };
        }
    });
  } finally {
    isCheckingInvoices.value = false;
  }
};

onMounted(async () => {
  await fetchStudents();
  organizeStudentsByFamily();
  await fetchAllInvoiceDetails();
});

</script> 